/**
 * GUI packs (`ui/src/packs/<id>/`): what a built-in plugin adds to the screen beyond its data,
 * as a game adds to the engine it is built on. A plugin's kinds, fields, views and windows come
 * from the core as data; a pack adds what data cannot say: board elements drawn its own way
 * (UI/UX's button, input, instance), groups of tools in the style bar, board modes (prototype
 * mode), what a frame is where it is worked in (a screen), windows (the Prototype) and a service
 * of its own.
 *
 * The engine (the board service, the board's windows) only offers these extension points and
 * never names a pack: `packs/index.ts` lists them, `app/` hands them to the services that take
 * them (elements to `kinds`, frames to `board`, given to the subsystems as `guiPacks` so their list
 * never loads a component) and starts them as one subsystem ({@link createPacks}),
 * and the board's windows draw what {@link Packs} gives. A pack takes part only where its
 * environment is worked in ({@link GuiPack.activeIn}); its elements are always known, so an
 * item drawn by one looks right in any environment.
 */
import { createMemo, createSignal, type Accessor, type Component } from "solid-js";
import type { Keybind } from "@protocol/Keybind";
import type { Node } from "@protocol/Node";
import type { CommandSet } from "../shared/commands";
import { BOARD_ELEMENTS, elementsOf, type ElementDef, type Elements } from "../shared/elements";
import type { MenuEntry, MenuSection } from "../shared/menus";
import type { Bar, Board, BoardFrame, FrameStyle, Point } from "./board";
import type { Environment } from "./environments";

/** The board mode where nothing of a pack's is: drawing and arranging. */
export const DESIGN = "design";

/** A drag a board mode's overlay started: from an item to the pointer (board units). */
export interface ModeDrag {
  from: string;
  to: Point;
}

/**
 * A board mode (Figma's prototype mode): the board shows and sets something else than the
 * design. While it is on, the selection's handles go, its overlay is drawn in the board's layer,
 * and the style bar shows only the sections made for it.
 */
export interface BoardMode {
  /** Its name (the board's `data-mode`, its button's `data-board-mode`). */
  id: string;
  /** What its button says it is. */
  title: string;
  /** The command switching to it and back (its shortcut is shown on the button). */
  command: string;
  /** Its button's picture. */
  Icon: Component;
  /**
   * Drawn in the board's layer, in board units, while the mode is on. A press on an element of
   * it marked `data-mode-drag=<item id>` starts a drag, shown to it as `dragging` and let go
   * through {@link BoardMode.drop}.
   */
  Overlay?: Component<{ dragging: ModeDrag | null }>;
  /** A drag from the overlay's handle on item `from`, let go at `at` (board units). */
  drop?(from: string, at: Point): Promise<void>;
  /** Classes an item takes while the mode is on. */
  itemClass?(node: Node): Record<string, boolean>;
}

/** A group of tools a pack adds to the style bar. */
export interface StyleSection {
  /** Its name. */
  id: string;
  /** The board mode it is for (none: designing). */
  mode?: string;
  /** Whether it shows for what is selected. */
  when(bar: Bar): boolean;
  /** The tools, reading and writing through the bar. */
  Tools: Component<{ bar: Bar }>;
}

/** What a pack draws on a frame: classes, and words beside its title. */
export interface FrameDecor {
  /** Classes the frame takes. */
  classes?(frame: BoardFrame): Record<string, boolean>;
  /** Before the title, and after it. */
  Before?: Component<{ frame: BoardFrame }>;
  After?: Component<{ frame: BoardFrame }>;
}

/** A window a pack brings (a `WindowDef` of `wm/manager.ts`, listed with the app's). */
export interface PackWindow {
  /** Stable name, used in layouts and configs. */
  id: string;
  /** Shown in the tab and the title bar. */
  title: string;
  /** The section of the Window menu it is listed in. */
  group?: string;
  /** The content (it reaches its pack's service with `usePack`). */
  component: Component<{ window: string }>;
}

/** What a started pack adds to the screen, made with its service. */
export interface PackParts {
  /** Board modes. */
  modes?: BoardMode[];
  /** Groups of tools in the style bar. */
  sections?: StyleSection[];
  /** What it draws on frames. */
  frames?: FrameDecor;
  /** Right-click menu lines for the selection, before the board's own (`at`: board units). */
  boardMenu?(at: Point, canEdit: boolean): MenuEntry[];
  /** Buttons at the end of the board's tool column. */
  Tools?: Component;
  /** What the empty board says. */
  hint?: string;
  /** Named commands (they check themselves whether the pack is worked in). */
  commands?: CommandSet;
  /** Sections of the menu bar's menus (their entries check it too). */
  menus?: MenuSection[];
}

/** What starting a pack gives: its service, and what it adds to the screen. */
export interface StartedPack<S> extends PackParts {
  /** The pack's own service. */
  service: S;
}

/** What a pack is started with besides the app's services. */
export interface PackContext {
  /** Whether it is worked in now. */
  active: Accessor<boolean>;
  /** The board mode in use, and choosing one. */
  mode: Accessor<string>;
  setMode(id: string): void;
}

/**
 * A GUI pack: the plugin it belongs to, where it takes part, what it adds before anything starts
 * (elements, frames, windows), and how it starts with the services it `needs` (`Services`: the
 * app's, `App` in `app/context.ts`).
 */
export interface GuiPack<Services, S = unknown> {
  /** The plugin it belongs to. */
  id: string;
  /** One line saying what it adds. */
  summary: string;
  /** Whether it takes part in `environment` (the one worked in; `null`: none). */
  activeIn(environment: Environment | null): boolean;
  /** Elements the board draws (always known: an item drawn by one looks right anywhere). */
  elements?: readonly ElementDef[];
  /** What a frame is where it takes part (a screen, made by a click at a device's size). */
  frames?: FrameStyle;
  /** Windows it brings (their names, like its commands', also listed by its plugin for the config). */
  windows?: readonly PackWindow[];
  /** The keys of its commands, kept while the config binds neither the key nor the command. */
  keys?: readonly Keybind[];
  /** The services it is started with. */
  needs: readonly (keyof Services & string)[];
  /** Starts it. */
  start(services: Services, context: PackContext): StartedPack<S>;
}

/** Every element the board draws: its own, then the packs'. */
export function packElements(packs: readonly GuiPack<never>[]): Elements {
  return elementsOf([...BOARD_ELEMENTS, ...packs.flatMap((p) => p.elements ?? [])]);
}

/** The keys every pack offers for its commands, in the list's order. */
export function packKeys(packs: readonly GuiPack<never>[]): Keybind[] {
  return packs.flatMap((p) => p.keys ?? []);
}

/** What a frame is in `environment`: the first pack taking part there that says so, else `null` (Miro's frame). */
export function packFrames(packs: readonly GuiPack<never>[], environment: Environment | null): FrameStyle | null {
  return packs.find((p) => p.frames && p.activeIn(environment))?.frames ?? null;
}

/** The needs of `packs` that are not among the services `given` (each named with its pack), so a pack never starts with a service missing. */
export function missingNeeds<Services>(packs: readonly GuiPack<Services>[], given: readonly string[]): string[] {
  return packs.flatMap((p) => p.needs.filter((n) => !given.includes(n)).map((n) => `${p.id} needs "${n}"`));
}

/** The packs, once started. */
export interface Packs {
  /** The ids of the packs taking part where this page works. */
  active: Accessor<string[]>;
  /** Pack `id`'s service (`undefined`: no such pack). */
  service<T>(id: string): T | undefined;
  /** The board modes the packs taking part offer (none: designing only). */
  modes: Accessor<BoardMode[]>;
  /** The board mode in use ({@link DESIGN}: none of theirs), and choosing one. */
  mode: Accessor<string>;
  setMode(id: string): void;
  /** The mode in use, if a pack's. */
  modeDef: Accessor<BoardMode | null>;
  /** The style bar's sections the packs taking part add, for the mode in use. */
  sections: Accessor<StyleSection[]>;
  /** What they draw on frames. */
  frameDecor: Accessor<FrameDecor[]>;
  /** Their right-click menu lines for the selection. */
  boardMenu(at: Point, canEdit: boolean): MenuEntry[];
  /** Their buttons at the end of the board's tool column. */
  tools: Accessor<Component[]>;
  /** What the empty board says (`null`: the board's own words). */
  hint: Accessor<string | null>;
  /** Every pack's commands and menu sections. */
  commands: CommandSet;
  menus: MenuSection[];
}

/** What the packs service is built from. */
export interface PacksDeps {
  /** The environment worked in. */
  environments: { current: Accessor<Environment | null> };
  /** The board, whose tool goes back to select when a mode starts. */
  board: Pick<Board, "setTool">;
}

/**
 * Starts `packs` with `services`, which hold the services named in `given` (the packs
 * subsystem's needs); throws, naming them, when a pack needs one that is not there.
 */
export function createPacks<Services extends PacksDeps>(packs: readonly GuiPack<Services>[], services: Services, given: readonly string[]): Packs {
  const missing = missingNeeds(packs, given);
  if (missing.length) throw new Error(`GUI packs start with what the packs subsystem needs (app/subsystems.ts): ${missing.join(", ")}`);
  const { environments, board } = services;
  const [chosen, setChosen] = createSignal(DESIGN);
  const activeIds = createMemo(() => packs.filter((p) => p.activeIn(environments.current())).map((p) => p.id), [], { equals: (a, b) => a.join() === b.join() });
  /** Each pack with what it added, in the list's order. */
  const started = packs.map((pack) => {
    const active = () => activeIds().includes(pack.id);
    const parts = pack.start(services, { active, mode: () => mode(), setMode });
    return { pack, parts, active };
  });
  const taking = () => started.filter((s) => s.active());
  const modes = createMemo(() => taking().flatMap((s) => s.parts.modes ?? []));
  const modeDef = createMemo(() => modes().find((m) => m.id === chosen()) ?? null);
  const mode = () => modeDef()?.id ?? DESIGN;
  function setMode(id: string) {
    setChosen(id);
    if (id !== DESIGN) board.setTool("select");
  }
  return {
    active: activeIds,
    service: <T>(id: string) => started.find((s) => s.pack.id === id)?.parts.service as T | undefined,
    modes,
    mode,
    setMode,
    modeDef,
    sections: createMemo(() => taking().flatMap((s) => (s.parts.sections ?? []).filter((section) => (section.mode ?? DESIGN) === mode()))),
    frameDecor: createMemo(() => taking().flatMap((s) => (s.parts.frames ? [s.parts.frames] : []))),
    boardMenu: (at, canEdit) => taking().flatMap((s) => s.parts.boardMenu?.(at, canEdit) ?? []),
    tools: createMemo(() => taking().flatMap((s) => (s.parts.Tools ? [s.parts.Tools] : []))),
    hint: () => taking().find((s) => s.parts.hint)?.parts.hint ?? null,
    commands: Object.assign({}, ...started.map((s) => s.parts.commands ?? {})) as CommandSet,
    menus: started.flatMap((s) => s.parts.menus ?? []),
  };
}
